import { useDispatch } from "react-redux";
import { employeesInterface } from ".";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { hideModal, showModal } from "store/modal/modalSlice";
import { useMutation, useQuery } from "react-query";
import { downloadCertificates, downloadSingleCertificate } from "services/certificate";
import { acceptUserAxios, fetchDepartments, getUserWorkplaces, getUsers } from "services/registration";
import { getCourseData } from "services/elearning";
import { Course } from "types/elearning";
import { useUser } from "hooks/useUser";
import { toast } from "react-toastify";
import { AxiosError, AxiosResponse } from "axios";
import { APIErrors } from "types/api";

type ModalType = 'generateLogin' | 'editData' | 'deleteUser' | 'addNewUser' | 'acceptUser' | 'changeWorkplace' | 'addNewUserSingle' | 'addNewUserMultiple' | 'editWorkplaceData' | 'downloadCertificatesSelect' | null;

export interface Workplace {
    department?: string;
    id: number;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
}

export const useEmployees = () => {
    const [employees, setEmployees] = useState<employeesInterface[]>([]);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [showTooltip, setShowTooltip] = useState<number | 'header' | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; bottomFromViewport: number; right: number; openUpward: boolean } | null>(null);
    const [modalType, setModalType] = useState<ModalType>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [selectedEmployees, setSelectedEmployees] = useState<employeesInterface[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<employeesInterface | null>(null);
    const dispatch = useDispatch();
    const { isAuthenticated, userData } = useUser();
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [page, setPage] = useState<number>(1);
    const [showFirstLogin, setShowFirstLogin] = useState<boolean>(true);
    const [loadMoreIsHide, setLoadMoreIsHide] = useState<boolean>(false);
    const [downloadCertificateModalType, setDownloadCertificateModalType] = useState<'bulk' | 'single' | null>(null);
    const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
    const [downloadModalEmployee, setDownloadModalEmployee] = useState<employeesInterface | null>(null);
    const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [selectedfrequencyOptions, setSelectedfrequencyOptions] = useState<string[]>([]);
    const [selectedcategoryOptions, setSelectedcategoryOptions] = useState<string[]>([]);
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const isFirstSearchRender = useRef(true);

    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(1);
            setEmployees([]);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const {
        data: userFromApi,
        isLoading,
        error
        } = useQuery(
        [
            'getUsers',
            userData?.email,
            page,
            refreshCounter,
            selectedWorkplace?.id,
            selectedDepartments,
            selectedfrequencyOptions,
            selectedcategoryOptions,
            debouncedSearch
        ],
        () =>
            getUsers({
                page,
                workplaceId: selectedWorkplace?.id,
                departmentIds: selectedDepartments.map(Number),
                lastActivity: selectedfrequencyOptions[0],
                courseIds: selectedcategoryOptions
                    .filter(v => v !== 'all' && v !== 'none')
                    .map(Number),
                withoutAnyCourseDone: selectedcategoryOptions.includes('none'),
                search: debouncedSearch || undefined,
            }),
        {
            enabled: !!userData?.email,
            staleTime: 0,
            cacheTime: 0,
            refetchOnWindowFocus: false,
            retry: false,
            onSuccess: (response: { data: employeesInterface[]; total: number }) => {
                const incoming = response.data ?? [];
                setEmployees(prev => {
                    const combined = page === 1 ? incoming : [...(prev ?? []), ...incoming];
                    const unique = Array.from(new Map(combined.map(e => [e.id, e])).values());
                    return unique;
                });
                setLoadMoreIsHide(incoming.length < 20);
            },
        }
    );

    const { data: adminWorkplaces = [], refetch: refetchWorkplaces } = useQuery(
        ['getUserWorkplaces', userData?.email],
        getUserWorkplaces,
        {
            enabled: !!userData?.email,
            staleTime: Infinity,
        }
    );

    const { data: totalUsersData } = useQuery(
        ['getUsersTotal', userData?.email, refreshCounter, selectedWorkplace?.id],
        () => getUsers({ page: 1, workplaceId: selectedWorkplace?.id }),
        {
            enabled: !!userData?.email && !!selectedWorkplace?.id,
            staleTime: 0,
            cacheTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );
    
    const total = totalUsersData?.total ?? 0;

    const { data: allCourses = [], isLoading: coursesLoading, error: coursesError } = useQuery<Course[]>(
        ['elearningCourses'],
        () => getCourseData(),
        {
            enabled: !!userData?.email,
            staleTime: Infinity,
            cacheTime: Infinity,
            refetchOnWindowFocus: false,
            retry: false,
        }
    );
    
    const WORKPLACE_STORAGE_KEY = 'selectedWorkplaceId';

    const handleWorkplaceChange = (workplace: Workplace) => {
        setPage(1);
        setEmployees([]);
        setSelectedWorkplace(workplace);
        localStorage.setItem(WORKPLACE_STORAGE_KEY, String(workplace.id));
    };

    const filteredEmployees = useMemo(() => {
        if (!employees) return [];

        return employees;
    }, [employees]);

    const handleChange = (e: { target: { value: string } }) => {
        setSearchTerm(e.target.value.toLowerCase());
    };

    const searchHandle = () => {
        const value = searchRef.current?.value.toLowerCase() || '';
        setSearchTerm(value);
    };

    const showHandle = (id: number | 'header', buttonEl?: HTMLElement) => {
        if (id === 'header' && selectedEmployees.length === 0) {
            setShowTooltip(null);
            setTooltipPosition(null);
            return;
        }

        const isOpening = showTooltip !== id;
        if (isOpening && buttonEl) {
            const rect = buttonEl.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setTooltipPosition({
                top: rect.bottom + 4,
                bottomFromViewport: window.innerHeight - rect.top + 4,
                right: window.innerWidth - rect.right,
                openUpward: spaceBelow < 160,
            });
        } else {
            setTooltipPosition(null);
        }

        setShowTooltip((prev) => (prev === id ? null : id));
    };

    const handleCheckboxChange = (employee: employeesInterface) => {
        setSelectedEmployees((prev) => {
            const isSelected = prev.some(e => e.id === employee.id);
            if (isSelected) {
                return prev.filter(e => e.id !== employee.id);
            } else {
                return [...prev, employee];
            }
        });
    };

    const handleToggleAll = () => {
        if (selectedEmployees.length === filteredEmployees.length) {
            setSelectedEmployees([]);
        } else {
            setSelectedEmployees(filteredEmployees);
        }
    };

    const sendLogins = () => {
        setShowTooltip(null);
        setTooltipPosition(null);
        setModalType('generateLogin');
        dispatch(showModal());
    }

    const deleteUsers = () => {
        setShowTooltip(null);
        setTooltipPosition(null);
        setModalType('deleteUser');
        dispatch(showModal());
    }

    useEffect(() => {
        dispatch(hideModal());
    }, []);

    useEffect(() => {
        if (showTooltip === null) {
            document.body.style.overflow = '';
        } else {
            document.body.style.overflow = 'hidden';
        }

        return () => { document.body.style.overflow = ''; };
    }, [showTooltip]);

    const hideModalHandle = (reloadUsers = false) => {
        setDownloadCertificateModalType(null);
        setSelectedCourseIds([]);
        setDownloadModalEmployee(null);

        dispatch(hideModal());

        if (reloadUsers) {
            setRefreshCounter(prev => prev + 1);
            setSelectedEmployees([]);
            refetchWorkplaces();
        }
    }

    const generateSingleLoginAndPassword = (employee: employeesInterface) => {
        setShowTooltip(null);
        setTooltipPosition(null);
        setModalType('generateLogin');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const editData = (employee: employeesInterface) => {
        setShowTooltip(null);
        setTooltipPosition(null);
        setModalType('editData');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const deleteUser = (employee: employeesInterface) => {
        setShowTooltip(null);
        setTooltipPosition(null);
        setModalType('deleteUser');
        setSelectedEmployees([employee]);
        dispatch(showModal());
    }

    const addNewUserShowPopup = () => {
        setModalType('addNewUser');
        dispatch(showModal());
    }

    const acceptserShowPopup = (employee: employeesInterface) => {
        setModalType('acceptUser');
        setSelectedEmployee(employee);
        dispatch(showModal());
    }

    const findWorkplaceId = () => {
        return selectedWorkplace?.id ?? 0;
    };

    const showMoreEmployees = () => {
        setPage((prev) => prev + 1);
    };

    const acceptUser = (employee: employeesInterface) => {
        acceptUserHandler.mutate(employee.id);
    }

    const acceptUserHandler = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        number
    >((data) => acceptUserAxios(data), {
        onSuccess: (data) => {
            if (data.status === 200) {
                toast.success(t('employeeManagement.acceptSuccessMessage'));
                setShowTooltip(null);
                setRefreshCounter(prev => prev + 1);
            }
        },
        onError: (error) => {
            toast.error(t('employeeManagement.acceptErrorMessage'));
            setShowTooltip(null);
        },
    });

    const downloadCertificatesHandler = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        { user_id?: number; user_ids?: number[]; elearning_course_ids: number[] }
    >(downloadCertificates, {
        onSuccess: (response) => {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificates.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('employeeManagement.downloadSuccessMessage'));
        },
        onError: (error) => {
            toast.error(t('employeeManagement.downloadErrorMessage'));
        },
    });

    const downloadSingleCertificateHandler = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        string
    >(downloadSingleCertificate, {
        onSuccess: (response) => {
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'certificate.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(t('employeeManagement.downloadSuccessMessage'));
        },
        onError: (error) => {
            toast.error(t('employeeManagement.downloadErrorMessage'));
        },
    });
    const prepareCertificateDownloadModal = (type: 'bulk' | 'single', employee?: employeesInterface) => {
        if (type === 'bulk') {
            if (!selectedEmployees.length) {
                toast.error(t('employeeManagement.noEmployeesSelected'));
                return;
            }

            if (selectedEmployees.length > 200) {
                toast.error(t('employeeManagement.tooManyEmployees'));
                return;
            }
        }

        const completedCourseIds = type === 'single' && employee
            ? (employee.elearning_analitics ?? []).filter(x => x.course_is_done === 1).map(x => x.elearning_course_id)
            : Array.from(new Set((selectedEmployees
                .flatMap(e => (e.elearning_analitics ?? []).filter(x => x.course_is_done === 1).map(x => x.elearning_course_id))
            )));

        setSelectedCourseIds(completedCourseIds);
        setDownloadCertificateModalType(type);
        setDownloadModalEmployee(type === 'single' ? employee ?? null : null);
        setModalType('downloadCertificatesSelect');
        dispatch(showModal());
    };

    const downloadCertificatesAction = () => {
        setShowTooltip(null);
        setTooltipPosition(null);
        prepareCertificateDownloadModal('bulk');
    };

    const downloadSingleCertificateAction = (employee: employeesInterface) => {
        setShowTooltip(null);
        setTooltipPosition(null);
        prepareCertificateDownloadModal('single', employee);
    };

    const cancelDownloadCertificateModal = () => {
        setDownloadCertificateModalType(null);
        setSelectedCourseIds([]);
        setDownloadModalEmployee(null);
        hideModalHandle();
    };

    const confirmDownloadCertificateModal = () => {
        if (!selectedCourseIds.length) {
            toast.error(t('employeeManagement.noCoursesSelected'));
            return;
        }

        if (downloadCertificateModalType === 'bulk') {
            const payload = {
                user_ids: selectedEmployees.map((e) => e.id),
                elearning_course_ids: selectedCourseIds,
            };
            downloadCertificatesHandler.mutate(payload);
        } else if (downloadCertificateModalType === 'single') {
            if (!downloadModalEmployee) {
                toast.error(t('employeeManagement.noEmployeesSelected'));
                return;
            }
            const payload = {
                user_ids: [downloadModalEmployee.id],
                elearning_course_ids: selectedCourseIds,
            };
            downloadCertificatesHandler.mutate(payload);
        }

        hideModalHandle();
    };

    const ellipsis = (str: string, max: number) => {
        if (!str) return '';
        return str.length > max ? str.slice(0, max) + '...' : str;
    }

    useEffect(() => {
        if (!adminWorkplaces.length) return;
        const savedId = localStorage.getItem(WORKPLACE_STORAGE_KEY);
        const savedWorkplace = savedId ? adminWorkplaces.find(w => w.id === Number(savedId)) : null;
        if (!selectedWorkplace) {
            setSelectedWorkplace(savedWorkplace ?? adminWorkplaces[0]);
        } else {
            const updated = adminWorkplaces.find(w => w.id === selectedWorkplace.id);
            if (updated) setSelectedWorkplace(updated);
        }
    }, [adminWorkplaces]);

    const changeWorkplaceShowPopup = () => {
        setModalType('changeWorkplace');
        dispatch(showModal());
    }

    const editWorkplaceShowPopup = () => {
        setModalType('editWorkplaceData');
        dispatch(showModal());
    }
    const [optionsDepartments, setOptionsDepartments] = useState<{ value: string; label: string }[]>([]);

    useEffect(() => {
        const workplaceId = selectedWorkplace?.id;
        if (!workplaceId) return;

        const fetchAllDepartments = async () => {
            let page = 1;
            let all: any[] = [];
            let lastPage = 1;
            do {
                const res = await fetchDepartments(workplaceId, page);
                const items: any[] = res?.data ?? res;
                all = [...all, ...items];
                lastPage = res?.last_page ?? 1;
                page++;
            } while (page <= lastPage);
            setOptionsDepartments(all.map(d => ({ value: String(d.id), label: d.name })));
        };

        fetchAllDepartments();
    }, [selectedWorkplace?.id]);

    const frequencyOptions = [
        { value: 'recent', label: t('employeeManagement.recent') },
        { value: 'never', label: t('employeeManagement.never') },
    ];

    const categoryOptions = [
        { value: 'all', label: t('employeeManagement.allLabel') },
        ...allCourses.map(c => ({ value: String(c.id), label: c.name })),
        { value: 'none', label: t('employeeManagement.noneLabel') },
    ];

    const handleSetSelectedDepartments = (val: string[]) => {
        unstable_batchedUpdates(() => {
            setSelectedDepartments(val);
            setPage(1);
            setEmployees([]);
        });
    };

    const activeFilters = [
        ...selectedDepartments.map(val => ({
            label: optionsDepartments.find(o => o.value === val)?.label ?? val,
            onRemove: () => handleSetSelectedDepartments(selectedDepartments.filter(v => v !== val)),
        })),
        ...selectedfrequencyOptions.map(val => ({
            label: frequencyOptions.find(o => o.value === val)?.label ?? val,
            onRemove: () => handleSetSelectedfrequencyOptions(selectedfrequencyOptions.filter(v => v !== val)),
        })),
        ...selectedcategoryOptions.map(val => ({
            label: categoryOptions.find(o => o.value === val)?.label ?? val,
            onRemove: () => handleSetSelectedcategoryOptions(selectedcategoryOptions.filter(v => v !== val)),
        })),
    ];

    const allCourseValues = categoryOptions.filter(v => v.value !== 'all' && v.value !== 'none').map(o => o.value);
    const categoryOptionsDisplayValue = allCourseValues.length > 0 && allCourseValues.every(v => selectedcategoryOptions.includes(v))
        ? [...selectedcategoryOptions, 'all']
        : selectedcategoryOptions;

    const handleSetSelectedcategoryOptions = (val: string[]) => {
        unstable_batchedUpdates(() => {
            const wasAllSelected = allCourseValues.length > 0 && allCourseValues.every(v => selectedcategoryOptions.includes(v));
            const wasNoneSelected = selectedcategoryOptions.includes('none');
            const newNone = val.includes('none');

            if (val?.includes('all') && !wasAllSelected) {
                // użytkownik zaznaczył 'all' → zaznacz wszystkie kursy, wyczyść 'none'
                setSelectedcategoryOptions(allCourseValues);
            } else if (wasAllSelected && !val.includes('all')) {
                // użytkownik odznaczył 'all' → wyczyść
                setSelectedcategoryOptions([]);
            } else if (newNone && !wasNoneSelected) {
                // użytkownik zaznaczył 'none' → odznacz wszystkie kursy, zostaw tylko 'none'
                setSelectedcategoryOptions(['none']);
            } else if (wasNoneSelected && !newNone) {
                // użytkownik odznaczył 'none' → wyczyść
                setSelectedcategoryOptions([]);
            } else {
                // normalne zaznaczanie/odznaczanie; usuń wirtualne 'all' i 'none' jeśli są razem z kursami
                const filtered = val.filter(v => v !== 'all');
                // jeśli zaznaczono kurs a 'none' było aktywne → usuń 'none'
                setSelectedcategoryOptions(filtered.filter(v => v !== 'none'));
            }
            setPage(1);
            setEmployees([]);
        });
    };

    const handleSetSelectedfrequencyOptions = (val: string[]) => {
        unstable_batchedUpdates(() => {
            setSelectedfrequencyOptions(val);
            setPage(1);
            setEmployees([]);
        });
    };

    const clearAllFilters = () => {
        unstable_batchedUpdates(() => {
            setSelectedDepartments([]);
            setSelectedfrequencyOptions([]);
            setSelectedcategoryOptions([]);
            setPage(1);
            setEmployees([]);
        });
    };

    const capitalizeFirstLetter = (text: string) => {
        if (!text) return '';
        return text.charAt(0).toUpperCase() + text.slice(1).toLocaleLowerCase();
    };

    return {
        navigate,
        t,
        handleChange,
        showHandle,
        handleCheckboxChange,
        handleToggleAll,
        sendLogins,
        deleteUsers,
        hideModalHandle,
        tooltipRef,
        showTooltip,
        tooltipPosition,
        selectedEmployees,
        setShowTooltip,
        searchTerm,
        searchHandle,
        searchRef,
        filteredEmployees,
        generateSingleLoginAndPassword,
        modalType,
        editData,
        deleteUser,
        isAuthenticated,
        addNewUserShowPopup,
        adminWorkplaces,
        findWorkplaceId,
        page,
        showMoreEmployees,
        selectedEmployee,
        acceptserShowPopup,
        showFirstLogin,
        setShowFirstLogin,
        loadMoreIsHide,
        setModalType,
        downloadCertificateModalType,
        selectedCourseIds,
        downloadModalEmployee,
        allCourses,
        coursesLoading,
        coursesError,
        setSelectedCourseIds,
        downloadCertificatesAction,
        downloadSingleCertificateAction,
        confirmDownloadCertificateModal,
        cancelDownloadCertificateModal,
        ellipsis,
        selectedWorkplace,
        changeWorkplaceShowPopup,
        handleWorkplaceChange,
        editWorkplaceShowPopup,
        employees,
        total,
        optionsDepartments,
        frequencyOptions,
        categoryOptions,
        selectedDepartments,
        setSelectedDepartments: handleSetSelectedDepartments,
        selectedfrequencyOptions,
        setSelectedfrequencyOptions: handleSetSelectedfrequencyOptions,
        selectedcategoryOptions,
        setSelectedcategoryOptions: handleSetSelectedcategoryOptions,
        categoryOptionsDisplayValue,
        activeFilters,
        clearAllFilters,
        capitalizeFirstLetter
    };
};
